import React, { useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import { TrendingUp, CreditCard, Dumbbell, Calendar } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { formatPrice } from '../utils/helpers';
import { format, subMonths } from 'date-fns';
import { ko } from 'date-fns/locale';

const COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444'];

const RevenueCard = ({ icon: Icon, label, value, color, sub }) => (
  <div className="card p-5">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
        <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
      </div>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-gray-100 dark:bg-gray-700`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
    </div>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg text-sm">
        <p className="font-semibold text-gray-900 dark:text-white mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }}>{p.name}: {formatPrice(p.value)}</p>
        ))}
      </div>
    );
  }
  return null;
};

const Revenue = () => {
  const { memberships, ptAssignments } = useApp();
  const [viewMode, setViewMode] = useState('monthly'); // monthly | yearly
  const [selectedYear, _setSelectedYear] = useState(new Date().getFullYear()); // eslint-disable-line no-unused-vars

  // 전체 결제 데이터 통합 (회원권 + PT)
  const allPayments = useMemo(() => {
    const ms = memberships.map(m => ({
      id: m.id,
      type: 'membership',
      label: '회원권',
      price: m.price || 0,
      date: m.createdAt || '',
    }));
    const pt = ptAssignments.map(p => ({
      id: p.id,
      type: 'pt',
      label: 'PT',
      price: p.price || 0,
      date: p.createdAt || '',
    }));
    return [...ms, ...pt].filter(p => p.date && p.price > 0);
  }, [memberships, ptAssignments]);

  // 월별 매출 집계 (최근 12개월)
  const monthlyData = useMemo(() => {
    const months = [];
    for (let i = 11; i >= 0; i--) {
      const d = subMonths(new Date(), i);
      const monthStr = format(d, 'yyyy-MM');
      const label = format(d, 'M월', { locale: ko });
      const msRevenue = allPayments.filter(p => p.type === 'membership' && p.date.startsWith(monthStr)).reduce((s, p) => s + p.price, 0);
      const ptRevenue = allPayments.filter(p => p.type === 'pt' && p.date.startsWith(monthStr)).reduce((s, p) => s + p.price, 0);
      months.push({ label, monthStr, membership: msRevenue, pt: ptRevenue, total: msRevenue + ptRevenue });
    }
    return months;
  }, [allPayments]);

  // 연별 매출 집계
  const yearlyData = useMemo(() => {
    const years = [];
    for (let y = selectedYear - 2; y <= selectedYear; y++) {
      const yearStr = String(y);
      const msRevenue = allPayments.filter(p => p.type === 'membership' && p.date.startsWith(yearStr)).reduce((s, p) => s + p.price, 0);
      const ptRevenue = allPayments.filter(p => p.type === 'pt' && p.date.startsWith(yearStr)).reduce((s, p) => s + p.price, 0);
      years.push({ label: `${y}년`, yearStr, membership: msRevenue, pt: ptRevenue, total: msRevenue + ptRevenue });
    }
    return years;
  }, [allPayments, selectedYear]);

  const chartData = viewMode === 'monthly' ? monthlyData : yearlyData;

  // 이번달 통계
  const thisMonth = format(new Date(), 'yyyy-MM');
  const lastMonth = format(subMonths(new Date(), 1), 'yyyy-MM');
  const thisMonthRevenue = allPayments.filter(p => p.date.startsWith(thisMonth)).reduce((s, p) => s + p.price, 0);
  const lastMonthRevenue = allPayments.filter(p => p.date.startsWith(lastMonth)).reduce((s, p) => s + p.price, 0);
  const thisMonthMs = allPayments.filter(p => p.type === 'membership' && p.date.startsWith(thisMonth)).reduce((s, p) => s + p.price, 0);
  const thisMonthPt = allPayments.filter(p => p.type === 'pt' && p.date.startsWith(thisMonth)).reduce((s, p) => s + p.price, 0);
  const totalRevenue = allPayments.reduce((s, p) => s + p.price, 0);

  const diff = thisMonthRevenue - lastMonthRevenue;
  const diffPct = lastMonthRevenue > 0 ? Math.round((diff / lastMonthRevenue) * 100) : 0;

  // 파이차트 데이터
  const pieData = [
    { name: '회원권', value: thisMonthMs },
    { name: 'PT', value: thisMonthPt },
  ].filter(d => d.value > 0);

  // 이번달 결제 내역
  const thisMonthPayments = allPayments
    .filter(p => p.date.startsWith(thisMonth))
    .sort((a, b) => b.date.localeCompare(a.date));

  return (
    <div className="space-y-6">
      {/* 요약 카드 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <RevenueCard
          icon={TrendingUp}
          label="이번달 총 매출"
          value={formatPrice(thisMonthRevenue)}
          color="text-blue-600 dark:text-blue-400"
          sub={diff >= 0 ? `▲ 전월 대비 +${diffPct}%` : `▼ 전월 대비 ${diffPct}%`}
        />
        <RevenueCard
          icon={CreditCard}
          label="이번달 회원권"
          value={formatPrice(thisMonthMs)}
          color="text-green-600 dark:text-green-400"
          sub={`${allPayments.filter(p => p.type === 'membership' && p.date.startsWith(thisMonth)).length}건`}
        />
        <RevenueCard
          icon={Dumbbell}
          label="이번달 PT"
          value={formatPrice(thisMonthPt)}
          color="text-purple-600 dark:text-purple-400"
          sub={`${allPayments.filter(p => p.type === 'pt' && p.date.startsWith(thisMonth)).length}건`}
        />
        <RevenueCard
          icon={Calendar}
          label="누적 총 매출"
          value={formatPrice(totalRevenue)}
          color="text-orange-600 dark:text-orange-400"
          sub={`총 ${allPayments.length}건`}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 매출 바 차트 */}
        <div className="card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900 dark:text-white">매출 추이</h2>
            <div className="flex gap-1">
              {[{ v: 'monthly', l: '월별' }, { v: 'yearly', l: '연별' }].map(({ v, l }) => (
                <button
                  key={v}
                  onClick={() => setViewMode(v)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${viewMode === v ? 'bg-primary-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          {chartData.every(d => d.total === 0) ? (
            <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
              매출 데이터가 없습니다.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.5} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tickFormatter={v => v >= 10000 ? `${Math.round(v / 10000)}만` : v} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="membership" name="회원권" fill="#3b82f6" radius={[3, 3, 0, 0]} stackId="a" />
                <Bar dataKey="pt" name="PT" fill="#8b5cf6" radius={[3, 3, 0, 0]} stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          )}
          <div className="flex gap-4 mt-2 justify-center">
            <span className="flex items-center gap-1 text-xs text-gray-500"><span className="w-3 h-3 rounded-sm bg-blue-500 inline-block" />회원권</span>
            <span className="flex items-center gap-1 text-xs text-gray-500"><span className="w-3 h-3 rounded-sm bg-purple-500 inline-block" />PT</span>
          </div>
        </div>

        {/* 이번달 비율 파이차트 */}
        <div className="card p-5">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-4">이번달 매출 구성</h2>
          {pieData.length === 0 ? (
            <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
              이번달 매출 없음
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3} dataKey="value">
                    {pieData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => formatPrice(v)} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-2">
                {pieData.map((d, i) => (
                  <div key={d.name} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <span className="w-3 h-3 rounded-sm inline-block" style={{ background: COLORS[i] }} />
                      {d.name}
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">{formatPrice(d.value)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* 이번달 결제 내역 */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="font-semibold text-gray-900 dark:text-white">
            이번달 결제 내역
            <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">({thisMonthPayments.length}건)</span>
          </h2>
        </div>
        {thisMonthPayments.length === 0 ? (
          <p className="text-center py-10 text-gray-400 text-sm">이번달 결제 내역이 없습니다.</p>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {thisMonthPayments.map(p => {
              const ms = p.type === 'membership' ? memberships.find(m => m.id === p.id) : null;
              const pt = p.type === 'pt' ? ptAssignments.find(a => a.id === p.id) : null;
              const name = ms?.productName || pt?.productName || '-';
              return (
                <div key={p.id} className="flex items-center justify-between px-5 py-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${p.type === 'membership' ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-purple-100 dark:bg-purple-900/30'}`}>
                      {p.type === 'membership'
                        ? <CreditCard className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        : <Dumbbell className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                      }
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{name}</p>
                      <p className="text-xs text-gray-400">{p.label} · {p.date}</p>
                    </div>
                  </div>
                  <p className="font-semibold text-gray-900 dark:text-white">{formatPrice(p.price)}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Revenue;
